import React, { memo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Amplify, Auth } from 'aws-amplify';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormLabel,
	Radio,
	RadioGroup,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil';
import { SnackbarProvider, useSnackbar } from 'notistack';

import {
	AudioTooltip,
	ControlDrawer,
	GameWaiting,
	LogViewer,
	ImageUploadCard,
	Tabletop,
	UserInfo,
	UserStream,
} from './../../components/index';
import { FAKE_STREAM, IS_LOCALHOST, CHAT_EVENTS } from './../../common/constants';
import { getUserLibraries } from '../../common/services/libraryRequests';
import {
	chatMessagesSelector,
	remoteChatSelector,
	removeChatChannelState,
} from './../../common/recoil/chatMessages/selectors';
import {
	adventureState,
	adventureBackgroundImageState,
	adventureBackgroundColorState,
} from '../../common/recoil/Adventures/AdventuresAtoms';
import { remoteChatState } from './../../common/recoil/chatMessages/atoms';
import {
	publicLibrariesState,
	privateLibrariesState,
	rulesLibraryState,
} from './../../common/recoil/Library/libraryAtom';
import { localStreamState, sharedAudiosState } from './../../common/recoil/Share/atom';
import { userIdState, userNameState, characterState, userAvatarState } from './../../common/recoil/UserInfo/atoms';
import {
	remoteConnectionHealthState,
	remoteNamesState,
	remoteMediaStreamsState,
	roomConnectionsState,
} from './../../common/recoil/websocket/atoms';
import {
	remoteMediaStreamsSelector,
	removeMediaStreamsSelector,
} from './../../common/recoil/websocket/selectors';

import { getAdventure } from '../../common/services/adventuresRequest';
import {
	getCharacter,
} from '../../common/services/characterRequests';
import { getAwsUserId } from '../../common/services/awsUserRequest';
import {
	getAvatar
} from './../../common/services/avatarsRequests';
import {
	getSessionValue,
	getWithExpiry,
	removeSessionValue,
	setSessionValue,
} from '../../common/utils';
import { debugSession } from '../../common/utils/sessionDebug';
import {
	getAdventureGameSystemType,
	isAlienAdventure,
} from '../../common/gameSystems/registry';

const Game = ({ client, retrieveLogs, id, setLocalMedia, setRoomConections, match }) => {
	const { roomId } = useParams();
	const { t } = useTranslation();
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const setId = useSetRecoilState(userIdState);
	const [userName, setUserName] = useRecoilState(userNameState);
	const localMedia = useRecoilValue(localStreamState);
	const sharedAudios = useRecoilValue(sharedAudiosState);
	const [adventure, setAdventure] = useRecoilState(adventureState);
	const setAdventureBackgroundImage = useSetRecoilState(adventureBackgroundImageState);
	const setAdventureBackgroundColor = useSetRecoilState(adventureBackgroundColorState);
	const [character, setCharacter] = useRecoilState(characterState);
	const setUserAvatar = useSetRecoilState(userAvatarState);
	const getGameReadyStorageKey = React.useCallback(
		(nextRoomId) => (nextRoomId ? `gameReady:${nextRoomId}` : null),
		[]
	);

	const [isGameReady, setIsGameReady] = React.useState(() => {
		const storageKey = getGameReadyStorageKey(roomId);
		if (!storageKey) {
			return false;
		}

		return getSessionValue(storageKey) === true || getWithExpiry(storageKey) === true;
	});

	const [roomConnections, setLocalRoomConnections] = useRecoilState(roomConnectionsState);
	const [remoteMediaStream, setRemoteMediaStream] = useRecoilState(remoteMediaStreamsState);
	const [remoteMediaStreamsGetSelector, setRemoteMediaStreamsSelector] = useRecoilState(
		remoteMediaStreamsSelector
	);
	const [remoteConnectionHealth, setRemoteConnectionHealth] = useRecoilState(
		remoteConnectionHealthState
	);
	const [remoteNames, setRemoteNames] = useRecoilState(remoteNamesState);
	const [removeMediaStreams, setRemoveMediaStreams] = useRecoilState(removeMediaStreamsSelector);
	const [remoteChat, setRemoteChat] = useRecoilState(remoteChatState);
	const [remoteChatS, addRemoteChat] = useRecoilState(remoteChatSelector);
	const [removeChatChannel, setRemoveChatChannel] = useRecoilState(removeChatChannelState);
	const [publicLibraries, setPublicLibraries] = useRecoilState(publicLibrariesState);
	const [privateLibraries, setPrivateLibraries] = useRecoilState(privateLibrariesState);
	const [rulesLibrary, setRulesLibrary] = useRecoilState(rulesLibraryState);
	const [chatMessages, setChatMessagesSelector] = useRecoilState(chatMessagesSelector);

	useEffect(() => {
		debugSession('Game', 'mount', {
			roomId: roomId || null,
			propId: id || null,
		});

		return () => {
			debugSession('Game', 'unmount', {
				roomId: roomId || null,
				propId: id || null,
			});
		};
	}, []);

	const removeStreamMedia = (id) => {
		const tempRemoteMediaStream = [...remoteMediaStream];
		setRemoteMediaStream(tempRemoteMediaStream.filter((x) => x.id !== id));
	};

	const removeDataChannel = (id) => {
		const tempRemoteChat = [...remoteChat];
		//setRemoteChat(tempRemoteChat.filter((x) => x.id !== id));
	};

	const useDrawerTableTop = false;

	// (async () => {
	// 	const currentSession = await Auth.currentSession();
	// 	console.log('idToken', currentSession.getIdToken().getJwtToken());
	// 	console.log('currentSession', currentSession);

	// 	const currentCredentials = await Auth.currentCredentials();
	// 	console.log('currentCredentials', currentCredentials);

	// 	const currentUserInfo = await Auth.currentUserInfo();
	// 	console.log('currentUserInfo', currentUserInfo);
	// })();

	useEffect(() => {
		debugSession('Game', 'roomId-check', {
			roomId: roomId || null,
		});
		if (!roomId) {
			setIsGameReady(false);
			return;
		}
		const storageKey = getGameReadyStorageKey(roomId);
		const savedSession = getSessionValue(storageKey);
		const savedLegacy = getWithExpiry(storageKey);
		const saved = savedSession ?? savedLegacy;
		debugSession('Game', 'game-ready-restored', {
			roomId,
			saved,
			source: savedSession != null ? 'session' : savedLegacy != null ? 'legacy-local' : null,
		});
		if (saved === true) {
			if (savedSession !== true) {
				setSessionValue(storageKey, true);
			}
			setIsGameReady(true);
		} else {
			removeSessionValue(storageKey);
			setIsGameReady(false);
		}
	}, [getGameReadyStorageKey, roomId]);

	useEffect(() => {
		debugSession('Game', 'game-ready-changed', {
			roomId: roomId || null,
			isGameReady,
		});
		const storageKey = getGameReadyStorageKey(roomId);
		if (!storageKey) {
			return;
		}
		if (isGameReady) {
			setSessionValue(storageKey, true);
			localStorage.removeItem(storageKey);
			return;
		}
		removeSessionValue(storageKey);
	}, [getGameReadyStorageKey, roomId, isGameReady]);

	useEffect(() => {
		debugSession('Game', 'boot-effects-start', {
			roomId: roomId || null,
		});
		const handleNewStreamMedia = function (e) {
			setRemoteMediaStreamsSelector({
				id: e.detail.id,
				streamMedia: e.detail.streamMedia,
				name: '',
			});
		};

		const handleNewDataChannel = function (e) {
			addRemoteChat({ ...e.detail });
		};

		const handleNewDataChannelMessage = function (e) {
			setChatMessagesSelector(e.detail.message);
		};

		const handleRemoveStream = function (e) {
			setRemoveMediaStreams(e.detail.id);
			// let tempRoomClients = [];
			// console.log('tempROom Clients start', [...tempRoomClients]);
			// tempRoomClients = tempRoomClients.filter((x) => {
			// 	console.log('tempROom ', x);

			// 	x.id !== e.detail.id;
			// });
			// console.log('tempROom Clients end', [...tempRoomClients]);
			setRemoteConnectionHealth((current) =>
				current.filter((connection) => connection.id !== e.detail.id)
			);
		};

		const handleConnectionHealthChanged = function (e) {
			setRemoteConnectionHealth((current) => {
				const filtered = current.filter((connection) => connection.id !== e.detail.id);
				return [
					...filtered,
					{
						id: e.detail.id,
						status: e.detail.status,
						message: e.detail.message,
					},
				];
			});
		};

		document.addEventListener('newStreamMedia', handleNewStreamMedia);
		document.addEventListener('newDataChannel', handleNewDataChannel);
		document.addEventListener('newDataChannelMessage', handleNewDataChannelMessage);
		document.addEventListener('removeStream', handleRemoveStream);
		document.addEventListener('connectionHealthChanged', handleConnectionHealthChanged);

		retrieveLibraries();
		retrieveAdventure();

		return () => {
			debugSession('Game', 'boot-effects-cleanup', {
				roomId: roomId || null,
			});
			document.removeEventListener('newStreamMedia', handleNewStreamMedia);
			document.removeEventListener('newDataChannel', handleNewDataChannel);
			document.removeEventListener('newDataChannelMessage', handleNewDataChannelMessage);
			document.removeEventListener('removeStream', handleRemoveStream);
			document.removeEventListener('connectionHealthChanged', handleConnectionHealthChanged);
		};
	}, []);

	const getCurrentUserCharacter = async (adventure) => {
		const awsUserId = await getAwsUserId();
		debugSession('Game', 'get-current-user-character', {
			roomId: roomId || null,
			awsUserId,
			playerCount: adventure.players?.length || 0,
		});
		if (adventure.players.some((id => id === awsUserId))) {
			//     "newPlayer0": {
			//         "characterId": "b9d97bf9-8379-433b-9499-c6fa349e00db",
			//         "playerId": "b1ac9c10-479d-4b27-9ae3-5000c7a9df30"
			//     },
			//     "newPlayer1": {
			//         "characterId": "b9d97bf9-8379-433b-9499-c6fa349e00db",
			//         "playerId": "5ee0777c-a98a-4f24-b4d8-4d037cf770ac"
			//     }
			// }
			const playerAssignment = Object.values(adventure.playerCharacterMap || {}).find(
				(player) => player?.playerId === awsUserId && player?.characterId
			);
			if (!playerAssignment?.characterId) {
				return false;
			}
			const characterId = playerAssignment.characterId;
			debugSession('Game', 'load-character-start', {
				roomId: roomId || null,
				characterId,
			});
			const character = await getCharacter(characterId);
			setCharacter(character);
			setUserName(character.name);
			const avatar = await getAvatar(character.avatarId)
			setUserAvatar(avatar);
			debugSession('Game', 'load-character-success', {
				roomId: roomId || null,
				characterId,
				avatarId: character.avatarId || null,
			});
			return true;
		}
		debugSession('Game', 'load-character-skipped', {
			roomId: roomId || null,
			awsUserId,
		});
		return false;
	}

	const isThisUserMaster = async (adventure) => {
		const awsUserId = await getAwsUserId();
		return adventure.masters?.some((id) => id === awsUserId) || false;
	};

	const getMasterDisplayName = (adventure) =>
		isAlienAdventure(adventure)
			? t('adventureEditor.fields.alienMother')
			: t('adventureEditor.fields.master');

	const retrieveAdventure = async () => {
		if (roomId) {
			debugSession('Game', 'retrieve-adventure-start', {
				roomId,
			});
			const adventure = await getAdventure(roomId);
			const gameSystemType = getAdventureGameSystemType(adventure);
			const alienAdventure = isAlienAdventure(adventure);
			debugSession('Game', 'retrieve-adventure-payload', {
				roomId,
				adventureId: adventure?.ID || null,
				type: adventure?.type ?? null,
				gameSystemId: adventure?.gameSystemId ?? null,
				resolvedGameSystemType: gameSystemType || null,
				isAlienAdventure: alienAdventure,
			});
			setAdventure(adventure);
			setAdventureBackgroundImage(adventure.visualProperties.backgroundImage);
			setAdventureBackgroundColor(adventure.visualProperties.backgroundColor);
			const loadedCharacter = await getCurrentUserCharacter(adventure);
			const isMaster = await isThisUserMaster(adventure)
			debugSession('Game', 'retrieve-adventure-success', {
				roomId,
				adventureId: adventure.ID || null,
				loadedCharacter,
				isMaster,
			});
			if (!loadedCharacter && isMaster) {
				setCharacter(null);
				setUserName(getMasterDisplayName(adventure));
			} else if (!loadedCharacter) {
				setCharacter(null);
				setUserName('');
			}
		}
	};

	const addFakeStream = () => {
		const name = `REMOTE-${remoteMediaStream.length}`;
		setRemoteMediaStreamsSelector({
			id: name,
			streamMedia: localMedia,
			name: name,
		});
		const newName = {
			id: name,
			name: name,
		};
		setRemoteNames([newName, ...remoteNames]);
	};

	useEffect(() => {
		if (remoteNames.length < 0 && IS_LOCALHOST && localMedia) {
			addFakeStream();
		}
	}, [remoteNames, localMedia]);

	useEffect(() => {
		if (id) {
			debugSession('Game', 'prop-id-changed', {
				roomId: roomId || null,
				id,
			});
			setId(id);
		}
	}, [id]);

	useEffect(() => {
		if (localMedia) {
			debugSession('Game', 'local-media-available', {
				roomId: roomId || null,
			});
			setLocalMedia(localMedia);
			setRoomConections(roomConnections);
		}
	}, [localMedia]);

	useEffect(() => {
		if (roomConnections) {
			debugSession('Game', 'room-connections-changed', {
				roomId: roomId || null,
				count: roomConnections.length,
			});
			setRoomConections(roomConnections);
		}
	}, [roomConnections]);

	useEffect(() => {
		debugSession('Game', 'remove-data-channel-listener-rebind', {
			roomId: roomId || null,
			remoteChatCount: remoteChat?.length || 0,
		});
		document.removeEventListener('removeDataChannel', function (e) {
			removeDataChannel(e.detail.id);
		});
		document.addEventListener('removeDataChannel', function (e) {
			removeDataChannel(e.detail.id);
		});
	}, [remoteChat]);

	const onClickDismiss = (key) => () => {
		closeSnackbar(key);
	};

	function warnAboutMessage(data) {
		try {
			const message = JSON.parse(data);
			if (message?.type === CHAT_EVENTS.CHAT_WARNING) {
				enqueueSnackbar(message?.text, {
					variant: 'warning',
					anchorOrigin: {
						vertical: 'bottom',
						horizontal: 'right',
					},
					action: (key) => (
						<Button onClick={onClickDismiss(key)}>
							<Close color={'white'}></Close>
						</Button>
					),
					preventDuplicate: true,
				});
			}
		} catch (e) {
			console.log('error parsing remote message');
		}
	}

	useEffect(() => {
		if (remoteChat) {
			debugSession('Game', 'remote-chat-changed', {
				roomId: roomId || null,
				remoteChatCount: remoteChat.length,
			});
			remoteChat.map((x) => {
				if (!x.channel.onmessage) {
					x.channel.onmessage = (event) => {
						warnAboutMessage(event.data);

						setChatMessagesSelector(event.data);
					};
				}
			});
		}
	}, [remoteChat]);

	const retrieveLibraries = async () => {
		const libraries = await getUserLibraries();
		setPublicLibraries(libraries.public || []);
		setPrivateLibraries(libraries.private || []);
		setRulesLibrary(libraries?.rules || []);
	};

	return (
		<div>
			<>
				{/* {userName === '' && <UserInfo></UserInfo>} */}
				{useDrawerTableTop ? (
					<Tabletop client={client}></Tabletop>
				) : isGameReady ? (
					<ControlDrawer client={client}></ControlDrawer>
				) : (
					<GameWaiting
						start={() => {
							setIsGameReady(true);
						}}></GameWaiting>
				)}
			</>
			<LogViewer retrieveLogs={retrieveLogs} />
			<Box visibility="hidden">
				<FormControl component="fieldset">
					<FormLabel component="legend"></FormLabel>
					<RadioGroup aria-label="" name="">
						<FormControlLabel control={<Radio />} />
						<FormControlLabel control={<Checkbox />} />
					</RadioGroup>
				</FormControl>
				<Button></Button>
				<ImageUploadCard cardName="Input Image"></ImageUploadCard>
			</Box>
		</div>
	);
};

export default memo(Game, () => false);
